import { ContentfulImageConfig, ContentfulImageParams } from "@2u-landing-pages/types";
import { Asset, AssetDetails } from "contentful";
import Image from "next/image";
import React, { Fragment } from "react";

const constructImageParams = (params: ContentfulImageParams) => {
  let queryParams = "";
  if (params.h) queryParams += `&h=${params.h}`;
  if (params.w) queryParams += `&w=${params.w}`;
  if (params.q) queryParams += `&q=${params.q}`;
  if (params.fit) queryParams += `&fit=${params.fit}`;
  if (params.f) queryParams += `&f=${params.f}`;
  if (params.r) queryParams += `&r=${params.r}`;
  return queryParams;
};

const calculateImageWidth = ({
  config,
  aspectRatio,
  originalWidth,
}: {
  config: ContentfulImageParams;
  aspectRatio: number;
  originalWidth: number;
}) => {
  if (config.w) return config.w;
  if (config.h) return Math.round(aspectRatio * config.h);
  return originalWidth;
};

const calculateImageHeight = ({
  config,
  aspectRatio,
  originalHeight,
}: {
  config: ContentfulImageParams;
  aspectRatio: number;
  originalHeight: number;
}) => {
  if (config.h) return config.h;
  if (config.w) return Math.round(config.w / aspectRatio);
  return originalHeight;
};

interface ContentfulImageProps {
  image?: Asset;
  config?: ContentfulImageConfig;
  className?: string;
  width?: number;
  style?: React.CSSProperties;
  loading?: "eager" | "lazy" | undefined;
}

export const ContentfulImage: React.FC<ContentfulImageProps> = ({ image, config, className, ...rest }) => {
  if (!image?.fields?.file?.url) return null;
  const isSvg = image.fields.file.contentType === "image/svg+xml";

  const imgDetails = image.fields.file.details as AssetDetails;

  let adjustedHeight = imgDetails.image?.height || 0;
  let adjustedWidth = imgDetails.image?.width || 0;
  const aspectRatio = adjustedWidth / adjustedHeight;

  if (config && config.default) {
    const { default: defaultConfig, mobile } = config;

    const defaultParams = constructImageParams(defaultConfig);
    const mobileParams = mobile ? constructImageParams(mobile) : {};

    return (
      <picture>
        {mobile &&
          (mobile?.fm || ["avif"])?.map((format) => {
            return (
              <source
                key={`mobile-${format}-${image?.sys?.id}`}
                srcSet={`https:${image?.fields?.file?.url}?${!isSvg ? "fm=" + format : ""}${mobileParams}`}
                type={`image/${format}`}
                height={calculateImageHeight({
                  config: mobile,
                  aspectRatio,
                  originalHeight: adjustedHeight,
                })}
                width={calculateImageWidth({
                  config: mobile,
                  aspectRatio,
                  originalWidth: adjustedWidth,
                })}
                media="(max-width: 1023px)"
              />
            );
          })}
        {(defaultConfig?.fm || ["avif"])?.map((format) => {
          const height = calculateImageHeight({
            config: defaultConfig,
            aspectRatio,
            originalHeight: adjustedHeight,
          });

          const width = calculateImageWidth({
            config: defaultConfig,
            aspectRatio,
            originalWidth: adjustedWidth,
          });

          const imageUrl = !isSvg
            ? `https:${image?.fields?.file?.url}?fm=${format}${defaultParams}`
            : `https:${image?.fields?.file?.url}`;

          return (
            <Fragment key={`default-${format}-${image?.sys?.id}`}>
              <source
                srcSet={imageUrl}
                type={`image/${format}`}
                height={height}
                width={width}
                media={mobile ? "(min-width: 1024px)" : undefined}
              />
              <Image
                className={className}
                src={imageUrl}
                alt={(image?.fields?.description as string) || ""}
                height={height}
                width={width}
                loading="lazy"
                {...rest}
                unoptimized={isSvg}
                style={{ borderRadius: "0" }}
              />
            </Fragment>
          );
        })}
      </picture>
    );
  }

  const imageFmtParams = {} as { [key: string]: string };

  imageFmtParams.fm = "avif";

  if (rest.width) {
    imageFmtParams.w = `${rest.width}`;
    adjustedWidth = rest.width;
    adjustedHeight = Math.round((rest.width / (adjustedWidth || 1)) * (adjustedHeight || 1));
  }

  const queryParams = new URLSearchParams(imageFmtParams).toString();
  const imageUrl =
    queryParams && !isSvg ? `https:${image.fields.file.url}?${queryParams}` : `https:${image.fields.file.url}`;

  return (
    <Image
      className={className}
      src={imageUrl}
      alt={(image?.fields?.description as string) || ""}
      height={adjustedHeight}
      width={adjustedWidth}
      loader={({ src }) => src}
      unoptimized={isSvg}
      {...rest}
    />
  );
};
